import {
  createContext,
  Dispatch,
  lazy,
  SetStateAction,
  Suspense,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import RouteKey from './key';
import AccessRole from '../pages/admin/AccessRole';
import InternalConfiguration from '../pages/admin/Configuration';
import RequestTaskList from '../pages/admin/RequestTaskList';
import TaskDetails from '../pages/admin/TaskDetails';

import OrgAdminResultReleaseList from '../pages/result/@org-admin/OrgAdminResultReleaseList';
import OrgAdminIndividualResultRelease from '../pages/result/@org-admin/OrgAdminIndividualResultRelease';
import TransactionLog from '../pages/admin/TransactionLog';
import IExamCandidateCreatePage from '../pages/admin/IExamCandidateCreatePage';
import FeaturesConfiguration from '../pages/admin/FeaturesConfiguration';
import OpAdminResitApply from '../pages/resit/@opadmin/OpAdminResitApply';
import OopAdminResitRegisterDetail from '../pages/resit/@opadmin/OopAdminResitRegisterDetail';
import ResultsAppeal from '../pages/admin/ResultsAppeal';
import OopAdminRegisteredCandidateDetails from '../pages/resit/@opadmin/OopAdminRegisteredCandidateDetails';
import ResultsAppealDetails from '../pages/admin/ResultsAppealDetails';
import PaymentReport from '../pages/admin/PaymentReport';
import CandidateBlacklistPage from '../pages/admin/CandidateBlacklistPage';
import AddCandidateBlacklistPage from '../pages/admin/AddCandidateBlacklistPage';
import AccessArrangementConfiguration from '../pages/admin/AccessArrangementConfiguration';
import AdminUniqueLink from '../pages/uniqueLink/AdminUniqueLink';
import RegistrationReport from '../pages/admin/RegistrationReport';
import ExamConfiguration from '../pages/admin/ExamConfiguration';
import NonNationalRegistrationReport from '../pages/admin/NonNationalRegistrationReport';
import AdminNonNationalExamApplication from '../pages/admin/AdminNonNationalExamApplication';
import ParentParticulars from '../pages/examRegistration/NonNationalExam/AEIS/ParentParticulars';
import NonNationalCandidateDetail from '../pages/examRegistration/NonNationalExam/SPERS/NonNationalCandidateDetail';
import ExamSchoolingDetails from '../pages/examRegistration/NonNationalExam/AEIS/ExamSchoolingDetails';
import UploadSupportingDocuments from '../pages/examRegistration/NonNationalExam/UploadSupportingDocuments';
import ConfirmParticulars from '../pages/examRegistration/NonNationalExam/ConfirmParticulars';
import AeisRegistrationSummaryOpsAdmin from '../pages/examRegistration/NonNationalExam/AEIS/AeisRegistrationSummaryOpsAdmin';
import SpersRegistrationSummaryOpsAdmin from '../pages/examRegistration/NonNationalExam/SPERS/SpersRegistrationSummaryOpsAdmin';
import SpersExamSchoolingDetails from '../pages/examRegistration/NonNationalExam/SPERS/SpersExamSchoolingDetails';
import UsefulInformationListing from '../pages/usefulInformation/@opadmin/UsefulInformationListing';
import OpsAdminResultAppealAccess from '../pages/appeal/@opsadmin/OpsAdminResultAppealAccess';
import AddResource from '../pages/usefulInformation/@opadmin/AddResource';
import EditResource from '../pages/usefulInformation/@opadmin/EditResource';
import SpecialConsiderationReport from '../pages/admin/SpecialConsiderationReport';
import AuditReport from '../pages/admin/AuditReport';
import AccessArrangementReport from '../pages/admin/AccessArrangementReport';
import OrgAdminNonNationalExaminationResults from '../pages/result/@org-admin/OrgAdminNonNationalExaminationResults';
import WithdrawalReason from '../pages/withdrawal/NonNationalExam/WithdrawalReason';
import ManageAnnouncementsListing from '../pages/manageAnnouncements/ManageAnnouncementsListing';
import WithdrawalSummaryOpsAdmin from '../pages/withdrawal/NonNationalExam/WithdrawalSummaryOpsAdmin';
import AnnouncementsConfiguration from '../pages/admin/AnnouncementsConfiguration';
import ViewNotificationDetails from '../pages/manageNotifications/ViewNotificationDetails/index';
import SendNotification from '../pages/manageNotifications/SendNotification/index';
import CreateEditTemplate from '../pages/manageNotifications/CreateEditTemplate';
import CreateEditAnnouncement from '../pages/manageAnnouncements/CreateNewAnnouncement/index';
import ManageNotificationsListing from '../pages/manageNotifications/ManageNotificationsListing';
import NotificationReport from '../pages/admin/NotificationReport';
import AdminEditAccessConfigurations from '../pages/userGroupAccessConfiguration/AdminEditAccessConfigurations';
import UserGroupListing from '../pages/userGroupAccessConfiguration/UserGroupListing/index';
import UserAccountManagementListing from '../pages/userAccountManagement/UserAccountManagementListing/index';
import AdminViewAccessConfigurations from '../pages/userGroupAccessConfiguration/AdminViewAccessConfigurations';
import AuditSettings from '../pages/admin/AuditSettings';
import CreateEditViewUserGroup from '../pages/userGroupAccessConfiguration/CreateEditViewUserGroup';
import UserAccessConfigurationWithFunctionList from '../pages/userAccessConfiguration/userAccessConfigurationWithFunctionList';
import CreateEditViewCoordinatorAccount from '../pages/userAccountManagement/CreateEditViewCoordinatorAccountDetails/index';
import CreateEditViewAdminAccount from '../pages/userAccountManagement/CreateEditViewAdminAccountDetails/index';
import DataArchival from '../pages/admin/DataArchival';
import CreateDataArchivalPage from '../pages/admin/CreateDataArchivalPage';
import ViewDataArchivalPage from '../pages/admin/ViewDataArchivalPage';
import DataArchivalReport from '../pages/admin/DataArchivalReport';
import ProfileAmendmentRequests from '../pages/admin/ProfileAmendmentRequests/ProfileAmendmentRequests';
import ReviewProfileAmendmentApplication from '../pages/admin/ProfileAmendmentRequests/ReviewProfileAmendmentApplication';
import JobStatusReport from '../pages/admin/JobStatusReport';
import JobStatusRecordDetails from '../pages/admin/JobStatusRecordDetails';
import AdminWithdrawalUniqueLink from '../pages/withdrawalUniqueLink/AdminUniqueLink';
import { HtmlTrans } from 'web-library';
import { NonNexPassportDisableContext } from './RootRouter';

const CreateEditViewCandidateAccount = lazy(
  () => import('../pages/userAccountManagement/CreateEditViewCandidateAccountDetails/index')
);

const { error: errorRoute } = RouteKey.child;

const {
  configuration,
  tasklist,
  viewDetails,
  requestlist,
  userAccessConfiguration,
  featuresConfiguration,
  auditSettings,
  accessRole,
  orgAdminResultRelease,
  orgAdminIndividualResult,
  transactionLog,
  iExams2CandidateCreate,
  oopAdminResitApply,
  oopAdminResitRegisteredExam,
  oopAdminIndividualResitRegisteredExam,
  opAdminResultsAppeal,
  opAdminResultsAppealDetails,
  opAdminPaymentReport,
  candidateBlacklist,
  addCandidateBlacklist,
  accessArrangementConfiguration,
  uniqueLink,
  withdrawalLink,
  opAdminRegistrationReport,
  examConfiguration,
  announcementsConfiguration,
  nationalReport,
  nonNationalReport,
  nonNationalExamApplication,
  parentParticulars,
  candidateDetails,
  examSchoolingDetails,
  uploadSupportingDocuments,
  confirmParticulars,
  summary,
  usefulInformation,
  oopAdminResultAppealAccess,
  specialConsiderationReport,
  auditReport,
  dataArchival,
  dataArchivalReport,
  opsAdminAccessArrangementReport,
  orgAdminNonNationalResult,
  nonNationalWithdrawalReason,
  manageAnnouncements,
  createNewAnnouncement,
  createNewBroadcastMessage,
  createNewFAQ,
  createNewAdvisoryBanner,
  createNewPaymentMessage,
  editAnnouncement,
  editBroadcastMessage,
  editFAQ,
  editAdvisoryBanner,
  editPaymentMessage,
  manageNotifications,
  sendEmailNotification,
  sendSMSNotification,
  editEmailNotification,
  editSMSNotification,
  createEmailTemplate,
  createSMSTemplate,
  editEmailTemplate,
  editSMSTemplate,
  viewEmailNotification,
  viewSMSNotification,
  withdrawal,
  notificationReport,
  userGroupAccessConfiguration,
  createUserGroup,
  editUserGroup,
  viewUserGroup,
  editAccessConfiguration,
  viewAccessConfiguration,
  userAccountManagement,
  adminAccountManagement,
  newDataArchival,
  viewDataArchival,
  profileAmendmentRequests,
  reviewProfileAmendmentApplication,
  jobStatusReport,
  viewRecordDetails,
} = RouteKey.admin.child;
const { addResource, editResource } = RouteKey['useful-information'].child;

interface OnlineTutorialsContextProps {
  isAddSuccess: boolean;
  setIsAddSuccess: Dispatch<SetStateAction<boolean>>;
  isEditSuccess: boolean;
  setIsEditSuccess: Dispatch<SetStateAction<boolean>>;
}

export const OnlineTutorialsContext = createContext<OnlineTutorialsContextProps>({
  isAddSuccess: false,
  isEditSuccess: false,
  setIsAddSuccess: () => {
    // Do Nothing
  },
  setIsEditSuccess: () => {
    // Do Nothing
  },
});

export const useOnlineTutorialsContext = () => useContext(OnlineTutorialsContext);

interface OnlineTutorialsRouteProps {
  path: string;
}

const OnlineTutorialsRoute = ({ path }: OnlineTutorialsRouteProps) => {
  const [isAddSuccess, setIsAddSuccess] = useState<boolean>(false);
  const [isEditSuccess, setIsEditSuccess] = useState<boolean>(false);
  const contextValue = useMemo(
    () => ({ isAddSuccess, isEditSuccess, setIsAddSuccess, setIsEditSuccess }),
    [isAddSuccess, isEditSuccess]
  );

  return (
    <OnlineTutorialsContext.Provider value={contextValue}>
      <Route exact path={`${path}/${usefulInformation.route}`}>
        <UsefulInformationListing />
      </Route>
      <Route exact path={`${path}/${usefulInformation.route}/${addResource.route}`}>
        <AddResource />
      </Route>

      <Route exact path={`${path}/${usefulInformation.route}/${editResource.route}/:uuid`}>
        <EditResource />
      </Route>
    </OnlineTutorialsContext.Provider>
  );
};

const getNonNationalExamStep = (t: TFunction) => [
  {
    label: t('exam->>NonNationalExam.ApplicationDeclaration'),
  },
  {
    label: t('exam->>NonNationalExam.ExamDetail'),
  },
  {
    label: t('exam->>NonNationalExam.UploadDocument'),
  },
  {
    label: t('exam->>NonNationalExam.ConfirmParticulars'),
  },
  {
    label: t('exam->>ExamRegistrationStepper.ReviewRegistration'),
  },
];

const getNonNationalExamPassportDisabledStep = (t: TFunction) => [
  {
    label: t('exam->>NonNationalExam.ApplicationDeclaration'),
  },
  {
    label: t('exam->>NonNationalExam.ExamDetail'),
  },
  {
    label: <HtmlTrans i18nKey="exam->>NonNationalExam.UploadDocumentDisabled" />,
    disabled: true,
  },
  {
    label: t('exam->>NonNationalExam.ConfirmParticulars'),
  },
  {
    label: t('exam->>ExamRegistrationStepper.ReviewRegistration'),
  },
];

const getNonNationalWithdrawalStep = (t: TFunction) => [
  {
    label: t('exam->>ExamRegistrationStepper.ReasonForWithdrawal'),
  },
  {
    label: t('exam->>ExamRegistrationStepper.ReviewRegistration'),
  },
];

const examTypes = ['aeis', 's-aeis', 'spe', 'spers-sec'];
const commonStepsRoutes = [
  examSchoolingDetails.route,
  uploadSupportingDocuments.route,
  confirmParticulars.route,
  summary.route,
];

const renderSummary = (
  isAEIS: boolean,
  isNotAEISAndPassportDisable: boolean,
  steps: { label: string }[],
  passportDisableSteps: { label: string | JSX.Element; disabled?: boolean }[]
) =>
  isAEIS ? (
    <AeisRegistrationSummaryOpsAdmin routeSteps={steps} />
  ) : (
    <SpersRegistrationSummaryOpsAdmin
      routeSteps={isNotAEISAndPassportDisable ? passportDisableSteps : steps}
    />
  );
const renderExamSchoolDetail = (
  isAEIS: boolean,
  isNotAEISAndPassportDisable: boolean,
  steps: { label: string }[],
  passportDisableSteps: { label: string | JSX.Element; disabled?: boolean }[]
) =>
  isAEIS ? (
    <ExamSchoolingDetails routeSteps={steps} isOpsAdmin />
  ) : (
    <SpersExamSchoolingDetails
      routeSteps={isNotAEISAndPassportDisable ? passportDisableSteps : steps}
      isOpsAdmin
    />
  );

const getStepsRoutes = (isAEIS: boolean) => [
  isAEIS ? parentParticulars.route : candidateDetails.route,
  ...commonStepsRoutes,
];

const nonNationalExaminationRoutes = (path: string, steps: { label: string }[]) =>
  examTypes.flatMap((examType) => {
    const isAEIS = ['aeis', 's-aeis'].includes(examType);
    const stepsRoutes = getStepsRoutes(isAEIS);
    const { t } = useTranslation();
    const nonNationalExamPassportDisabledStep = getNonNationalExamPassportDisabledStep(t);
    const nonNexPassportDisableContext = useContext(NonNexPassportDisableContext);
    const isNotAEISAndPassportDisable =
      !isAEIS && nonNexPassportDisableContext.isPassportDisabled.valueOf();
    return stepsRoutes.map((stepRoute) => ({
      fullPath: `${path}/${nonNationalExamApplication.route}/:uuid/${examType}/${stepRoute}`,
      component: (
        <>
          {stepRoute === parentParticulars.route && (
            <ParentParticulars routeSteps={steps} isOpsAdmin />
          )}
          {stepRoute === candidateDetails.route && (
            <NonNationalCandidateDetail
              routeSteps={isNotAEISAndPassportDisable ? nonNationalExamPassportDisabledStep : steps}
              isOpsAdmin
            />
          )}
          {stepRoute === examSchoolingDetails.route &&
            renderExamSchoolDetail(
              isAEIS,
              isNotAEISAndPassportDisable,
              steps,
              nonNationalExamPassportDisabledStep
            )}
          {stepRoute === uploadSupportingDocuments.route && (
            <UploadSupportingDocuments
              routeSteps={isNotAEISAndPassportDisable ? nonNationalExamPassportDisabledStep : steps}
              isAEIS={isAEIS}
              isOpsAdmin
            />
          )}
          {stepRoute === confirmParticulars.route && (
            <ConfirmParticulars
              routeSteps={isNotAEISAndPassportDisable ? nonNationalExamPassportDisabledStep : steps}
              isAEIS={isAEIS}
              isOpsAdmin
            />
          )}
          {stepRoute === summary.route &&
            renderSummary(
              isAEIS,
              isNotAEISAndPassportDisable,
              steps,
              nonNationalExamPassportDisabledStep
            )}
        </>
      ),
    }));
  });

const AdminRoute = () => {
  const { path } = useRouteMatch();
  const { t } = useTranslation();
  const nonNationalExamSteps = getNonNationalExamStep(t);
  const nonNationalWithdrawalSteps = getNonNationalWithdrawalStep(t);

  return (
    <Switch>
      <Route exact path={`${path}`}>
        <Redirect to={`${path}/${configuration.route}`} />
      </Route>

      <Route path={`${path}/${configuration.route}`}>
        <InternalConfiguration />
      </Route>

      <Route exact path={`${path}/${tasklist.route}`}>
        <RequestTaskList />
      </Route>

      <Route path={`${path}/${tasklist.route}/:uuid/${viewDetails.route}`}>
        <TaskDetails />
      </Route>

      <Route exact path={`${path}/${requestlist.route}`}>
        <RequestTaskList isRequesterFlow />
      </Route>

      <Route path={`${path}/${requestlist.route}/:uuid/${viewDetails.route}`}>
        <TaskDetails />
      </Route>

      <Route path={`${path}/${userAccessConfiguration.route}`}>
        <UserAccessConfigurationWithFunctionList />
      </Route>

      <Route path={`${path}/${featuresConfiguration.route}`}>
        <FeaturesConfiguration />
      </Route>

      <Route path={`${path}/${auditSettings.route}`}>
        <AuditSettings />
      </Route>

      <Route path={`${path}/${transactionLog.route}`}>
        <TransactionLog />
      </Route>

      <Route path={`${path}/${iExams2CandidateCreate.route}`}>
        <IExamCandidateCreatePage />
      </Route>

      <Route path={`${path}/${accessRole.route}`}>
        <AccessRole />
      </Route>

      <Route path={`${path}/${orgAdminResultRelease.route}`} exact>
        <OrgAdminResultReleaseList />
      </Route>

      <Route path={`${path}/${orgAdminNonNationalResult.route}`}>
        <OrgAdminNonNationalExaminationResults />
      </Route>

      <Route
        path={`${path}/${orgAdminResultRelease.route}/${orgAdminIndividualResult.route}`}
        exact
      >
        <OrgAdminIndividualResultRelease />
      </Route>

      <Route exact path={`${path}/${profileAmendmentRequests.route}`}>
        <ProfileAmendmentRequests />
      </Route>

      <Route
        exact
        path={`${path}/${profileAmendmentRequests.route}/:uuid/${reviewProfileAmendmentApplication.route}`}
      >
        <ReviewProfileAmendmentApplication />
      </Route>

      {/** Ops Admin Resit Unique link */}
      <Route path={`${path}/${oopAdminResitApply.route}`}>
        <OpAdminResitApply />
      </Route>

      {/** Ops Admin Resit Candidate Registered Details */}
      <Route exact path={`${path}/${oopAdminResitRegisteredExam.route}`}>
        <OopAdminResitRegisterDetail />
      </Route>
      {/** Ops Admin Individual Resit Candidate Registered Details */}
      <Route
        exact
        path={`${path}/${oopAdminResitRegisteredExam.route}/${oopAdminIndividualResitRegisteredExam.route}`}
      >
        <OopAdminRegisteredCandidateDetails />
      </Route>

      {/** Ops Admin Result Appeal Details */}
      <Route exact path={`${path}/${opAdminResultsAppeal.route}`}>
        <ResultsAppeal />
      </Route>

      <Route exact path={`${path}/${nonNationalExamApplication.route}`}>
        <AdminNonNationalExamApplication />
      </Route>

      <Route
        exact
        path={`${path}/${opAdminResultsAppeal.route}/${opAdminResultsAppealDetails.route}`}
      >
        <ResultsAppealDetails />
      </Route>

      {/** Ops Admin Registration Report */}
      <Route exact path={`${path}/${opAdminRegistrationReport.route}/${nationalReport.route}`}>
        <RegistrationReport />
      </Route>

      <Route exact path={`${path}/${opAdminRegistrationReport.route}/${nonNationalReport.route}`}>
        <NonNationalRegistrationReport />
      </Route>

      <Route exact path={`${path}/${opAdminPaymentReport.route}`}>
        <PaymentReport />
      </Route>

      <Route exact path={`${path}/${specialConsiderationReport.route}`}>
        <SpecialConsiderationReport />
      </Route>

      <Route exact path={`${path}/${auditReport.route}`}>
        <AuditReport />
      </Route>

      <Route exact path={`${path}/${dataArchival.route}`}>
        <DataArchival />
      </Route>
      <Route exact path={`${path}/${dataArchival.route}/${newDataArchival.route}`}>
        <CreateDataArchivalPage />
      </Route>
      <Route exact path={`${path}/${dataArchival.route}/:uuid/${viewDataArchival.route}`}>
        <ViewDataArchivalPage />
      </Route>

      <Route exact path={`${path}/${dataArchivalReport.route}`}>
        <DataArchivalReport />
      </Route>

      <Route exact path={`${path}/${uniqueLink.route}/:nationalExamType`}>
        <AdminUniqueLink />
      </Route>

      <Route exact path={`${path}/${withdrawalLink.route}/:nationalExamType`}>
        <AdminWithdrawalUniqueLink />
      </Route>

      <Route exact path={`${path}/${candidateBlacklist.route}`}>
        <CandidateBlacklistPage />
      </Route>

      <Route exact path={`${path}/${candidateBlacklist.route}/${addCandidateBlacklist.route}`}>
        <AddCandidateBlacklistPage />
      </Route>

      <Route exact path={`${path}/${accessArrangementConfiguration.route}`}>
        <AccessArrangementConfiguration />
      </Route>

      <Route exact path={`${path}/${notificationReport.route}`}>
        <NotificationReport />
      </Route>

      <Route exact path={`${path}/${examConfiguration.route}`}>
        <ExamConfiguration />
      </Route>

      <Route exact path={`${path}/${announcementsConfiguration.route}`}>
        <AnnouncementsConfiguration />
      </Route>

      <Route path={`${path}/${oopAdminResultAppealAccess.route}`}>
        <OpsAdminResultAppealAccess />
      </Route>

      <Route exact path={`${path}/${opsAdminAccessArrangementReport.route}`}>
        <AccessArrangementReport />
      </Route>

      <Route exact path={`${path}/${manageAnnouncements.route}`}>
        <ManageAnnouncementsListing />
      </Route>

      <Route exact path={`${path}/${manageAnnouncements.route}/${createNewAnnouncement.route}`}>
        <CreateEditAnnouncement isEdit={false} />
      </Route>

      <Route exact path={`${path}/${manageAnnouncements.route}/${createNewBroadcastMessage.route}`}>
        <CreateEditAnnouncement isEdit={false} />
      </Route>

      <Route exact path={`${path}/${manageAnnouncements.route}/${createNewFAQ.route}`}>
        <CreateEditAnnouncement isEdit={false} />
      </Route>

      <Route exact path={`${path}/${manageAnnouncements.route}/${createNewAdvisoryBanner.route}`}>
        <CreateEditAnnouncement isEdit={false} />
      </Route>

      <Route exact path={`${path}/${manageAnnouncements.route}/${createNewPaymentMessage.route}`}>
        <CreateEditAnnouncement isEdit={false} />
      </Route>

      <Route exact path={`${path}/${manageAnnouncements.route}/:uuid/${editAnnouncement.route}`}>
        <CreateEditAnnouncement isEdit />
      </Route>

      <Route
        exact
        path={`${path}/${manageAnnouncements.route}/:uuid/${editBroadcastMessage.route}`}
      >
        <CreateEditAnnouncement isEdit />
      </Route>

      <Route exact path={`${path}/${manageAnnouncements.route}/:uuid/${editFAQ.route}`}>
        <CreateEditAnnouncement isEdit />
      </Route>

      <Route exact path={`${path}/${manageAnnouncements.route}/:uuid/${editAdvisoryBanner.route}`}>
        <CreateEditAnnouncement isEdit />
      </Route>

      <Route exact path={`${path}/${manageAnnouncements.route}/:uuid/${editPaymentMessage.route}`}>
        <CreateEditAnnouncement isEdit />
      </Route>

      <Route exact path={`${path}/${manageNotifications.route}`}>
        <ManageNotificationsListing />
      </Route>

      <Route
        exact
        path={`${path}/${manageNotifications.route}/:uuid/${viewEmailNotification.route}`}
      >
        <ViewNotificationDetails />
      </Route>

      <Route exact path={`${path}/${manageNotifications.route}/:uuid/${viewSMSNotification.route}`}>
        <ViewNotificationDetails />
      </Route>

      <Route exact path={`${path}/${manageNotifications.route}/${createEmailTemplate.route}`}>
        <CreateEditTemplate isEdit={false} />
      </Route>

      <Route exact path={`${path}/${manageNotifications.route}/${createSMSTemplate.route}`}>
        <CreateEditTemplate isEdit={false} />
      </Route>

      <Route exact path={`${path}/${manageNotifications.route}/:uuid/${editEmailTemplate.route}`}>
        <CreateEditTemplate isEdit />
      </Route>

      <Route exact path={`${path}/${manageNotifications.route}/:uuid/${editSMSTemplate.route}`}>
        <CreateEditTemplate isEdit />
      </Route>

      <Route
        exact
        path={`${path}/${manageNotifications.route}/:uuid/${sendEmailNotification.route}`}
      >
        <SendNotification isEdit={false} />
      </Route>

      <Route exact path={`${path}/${manageNotifications.route}/:uuid/${sendSMSNotification.route}`}>
        <SendNotification isEdit={false} />
      </Route>

      <Route
        exact
        path={`${path}/${manageNotifications.route}/:uuid/${editEmailNotification.route}`}
      >
        <SendNotification isEdit />
      </Route>

      <Route exact path={`${path}/${manageNotifications.route}/:uuid/${editSMSNotification.route}`}>
        <SendNotification isEdit />
      </Route>

      {/* Start For Non National Examinations */}
      {nonNationalExaminationRoutes(path, nonNationalExamSteps).map((route) => (
        <Route exact path={route.fullPath} key={path}>
          {route.component}
        </Route>
      ))}

      {/* Start For Non National Withdrawal */}
      <Route
        exact
        path={`${path}/${nonNationalExamApplication.route}/:uuid/${withdrawal.route}/${nonNationalWithdrawalReason.route}`}
      >
        <WithdrawalReason routeSteps={nonNationalWithdrawalSteps} isOpsAdmin />
      </Route>

      <Route
        exact
        path={`${path}/${nonNationalExamApplication.route}/:uuid/${withdrawal.route}/${summary.route}`}
      >
        <WithdrawalSummaryOpsAdmin routeSteps={nonNationalWithdrawalSteps} />
      </Route>

      <Route exact path={`${path}/${userGroupAccessConfiguration.route}`}>
        <UserGroupListing />
      </Route>

      <Route exact path={`${path}/${userGroupAccessConfiguration.route}/${createUserGroup.route}`}>
        <CreateEditViewUserGroup isCreate isView={false} />
      </Route>

      <Route
        exact
        path={`${path}/${userGroupAccessConfiguration.route}/:uuid/${editUserGroup.route}`}
      >
        <CreateEditViewUserGroup isCreate={false} isView={false} />
      </Route>

      <Route
        exact
        path={`${path}/${userGroupAccessConfiguration.route}/:uuid/${viewUserGroup.route}`}
      >
        <CreateEditViewUserGroup isCreate={false} isView />
      </Route>

      <Route
        exact
        path={`${path}/${userGroupAccessConfiguration.route}/:uuid/${editAccessConfiguration.route}`}
      >
        <AdminEditAccessConfigurations />
      </Route>

      <Route
        exact
        path={`${path}/${userGroupAccessConfiguration.route}/:uuid/${viewAccessConfiguration.route}`}
      >
        <AdminViewAccessConfigurations />
      </Route>

      <Route exact path={`${path}/${userAccountManagement.route}`}>
        <UserAccountManagementListing isOpsAdmin={false} />
      </Route>

      <Route exact path={`${path}/${adminAccountManagement.route}`}>
        <UserAccountManagementListing isOpsAdmin />
      </Route>

      <Route exact path={`${path}/${userAccountManagement.route}/candidate`}>
        <Suspense fallback={null}>
          <CreateEditViewCandidateAccount isEdit={false} />
        </Suspense>
      </Route>

      <Route
        exact
        path={`${path}/${userAccountManagement.route}/:uuid/candidate/${viewDetails.route}`}
      >
        <Suspense fallback={null}>
          <CreateEditViewCandidateAccount isEdit />
        </Suspense>
      </Route>

      <Route exact path={`${path}/${userAccountManagement.route}/coordinator`}>
        <CreateEditViewCoordinatorAccount isEdit={false} isCreate />
      </Route>

      <Route
        exact
        path={`${path}/${userAccountManagement.route}/:uuid/coordinator/${viewDetails.route}`}
      >
        <CreateEditViewCoordinatorAccount isEdit isCreate={false} />
      </Route>

      <Route exact path={`${path}/${adminAccountManagement.route}/admin`}>
        <CreateEditViewAdminAccount isEdit={false} isCreate />
      </Route>

      <Route exact path={`${path}/${adminAccountManagement.route}/:uuid/${viewDetails.route}`}>
        <CreateEditViewAdminAccount isEdit isCreate={false} />
      </Route>

      <Route exact path={`${path}/${jobStatusReport.route}`}>
        <JobStatusReport />
      </Route>

      <Route exact path={`${path}/${jobStatusReport.route}/${viewRecordDetails.route}/:uuid`}>
        <JobStatusRecordDetails />
      </Route>

      <OnlineTutorialsRoute path={path} />

      {/* End For Non National Examinations */}
      <Route path="*" exact>
        <Redirect to={`/${errorRoute}`} />
      </Route>
    </Switch>
  );
};

export default AdminRoute;
